import React, { useEffect, useState } from 'react';
import DataTable from './DataTable';
import MyAccountPageShell from './MyAccountPageShell';
import { LoadingBar, PageShellLoading, TableSkeleton, ToolbarSkeleton } from './MyAccountLoadingStates';

interface CollateralItem {
    id: number;
    date: string;
    name: string;
    file_type: string;
    file_url: string;
    thumbnail_url?: string;
    featured?: boolean;
}

const BrandCollateral: React.FC = () => {
    const [collateral, setCollateral] = useState<CollateralItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        loadCollateral();
    }, []);

    const loadCollateral = async () => {
        try {
            setLoading(true);
            setError(null);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch('/wp-admin/admin-ajax.php?action=get_brand_collateral', {
                method: 'GET',
                credentials: 'same-origin',
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            const result = await response.json();
            if (result.success && result.data) {
                setCollateral(result.data);
            } else {
                setError('Failed to load brand collateral');
            }
        } catch (err: any) {
            console.error('Failed to fetch brand collateral:', err);
            if (err.name === 'AbortError') {
                setError('Request timeout - please check your connection and try again');
            } else {
                setError('Error loading brand collateral');
            }
        } finally {
            setLoading(false);
        }
    };

    // Filter collateral based on search term
    const filteredCollateral = collateral.filter(item => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            item.name.toLowerCase().includes(search) ||
            item.date.toLowerCase().includes(search) ||
            item.file_type.toLowerCase().includes(search)
        );
    });

    // Get featured items
    const featuredCollateral = collateral.filter(item => item.featured);

    if (loading) {
        return (
            <PageShellLoading
                showMeta={true}
                titleWidth="30%"
                subtitleWidth="58%"
                metaWidth="148px"
            >
                <ToolbarSkeleton />
                <TableSkeleton columns={3} rows={8} />
                <div className="mt-4">
                    <LoadingBar height="20px" width="24%" style={{ marginBottom: '12px' }} />
                    <div className="row g-3">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <div key={`brand-featured-skeleton-${index}`} className="col-md-3">
                                <div className="skeleton" style={{ height: '220px', borderRadius: '10px' }} />
                            </div>
                        ))}
                    </div>
                </div>
            </PageShellLoading>
        );
    }

    if (error) {
        return (
            <MyAccountPageShell
                title="Brand Collateral"
                subtitle="Browse approved brand assets and downloadable collateral."
            >
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                    <button onClick={loadCollateral} className="btn btn-primary">Retry</button>
            </MyAccountPageShell>
        );
    }

    return (
        <MyAccountPageShell
            title="Brand Collateral"
            subtitle="Browse approved brand assets and downloadable collateral."
            meta={filteredCollateral.length === collateral.length
                ? `${collateral.length} collateral items`
                : `Showing ${filteredCollateral.length} of ${collateral.length} collateral items`}
        >
                <div className="mb-4">
                    <DataTable
                        rows={filteredCollateral}
                        rowKey={(item) => item.id}
                        wrapperClassName=""
                        responsiveMode="stacked-cards"
                        tableClassName="table table-hover mb-0"
                        tableStyle={{ width: '100%', borderCollapse: 'collapse' }}
                        toolbar={{
                            searchValue: searchTerm,
                            onSearchChange: (value) => {
                                setSearchTerm(value);
                                setCurrentPage(1);
                            },
                            entriesPerPage,
                            onEntriesPerPageChange: (value) => {
                                setEntriesPerPage(value);
                                setCurrentPage(1);
                            },
                            searchPlaceholder: 'Search collateral...',
                            filteredCount: filteredCollateral.length,
                            totalCount: collateral.length,
                            countLabel: 'collateral items',
                            onClearSearch: () => {
                                setSearchTerm('');
                                setCurrentPage(1);
                            },
                            clearSearchLabel: 'Clear search',
                        }}
                        pagination={{
                            currentPage,
                            itemsPerPage: entriesPerPage,
                            totalItems: filteredCollateral.length,
                            itemsLabel: 'entries',
                            onPageChange: setCurrentPage,
                            mode: 'simple',
                        }}
                        rowStyle={{
                            borderBottom: '1px solid var(--weka-subtle-border-color)',
                        }}
                        columns={[
                            {
                                id: 'date',
                                header: 'DATE',
                                mobileLabel: 'Date',
                                mobileDisplay: 'field',
                                headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: '#495057' },
                                cellStyle: { padding: '12px' },
                                render: (item) => item.date,
                            },
                            {
                                id: 'name',
                                header: 'NAME',
                                mobileLabel: 'Name',
                                mobileDisplay: 'title',
                                headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: '#495057' },
                                cellStyle: { padding: '12px' },
                                render: (item) => (
                                    <a
                                        href={item.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: 'var(--weka-link-color)', textDecoration: 'none' }}
                                    >
                                        {item.name}
                                    </a>
                                ),
                            },
                            {
                                id: 'file_type',
                                header: 'FILE TYPE',
                                mobileLabel: 'File type',
                                mobileDisplay: 'field',
                                headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: '#495057' },
                                cellStyle: { padding: '12px' },
                                render: (item) => item.file_type,
                            },
                        ]}
                        emptyState={
                            <div className="text-center py-3">
                                No collateral found
                            </div>
                        }
                    />
                </div>

                {featuredCollateral.length > 0 && (
                    <div>
                        <h3 className="weka-my-account-page__section-title">Featured Collateral</h3>
                        <div className="weka-brand-collateral-featured-grid">
                            {featuredCollateral.map((item) => (
                                <div key={item.id} className="weka-brand-collateral-featured-item">
                                    <div className="weka-brand-collateral-featured-media">
                                        {item.thumbnail_url ? (
                                            <img
                                                src={item.thumbnail_url}
                                                alt={item.name}
                                            />
                                        ) : (
                                            <div className="weka-brand-collateral-featured-placeholder">
                                                <span aria-hidden="true">PDF</span>
                                            </div>
                                        )}
                                    </div>
                                    <h4 className="weka-brand-collateral-featured-name">{item.name}</h4>
                                    <a
                                        href={item.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-outline-secondary"
                                    >
                                        View
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
        </MyAccountPageShell>
    );
};

export default BrandCollateral;
